import { create } from 'zustand';
import { persist, devtools } from 'zustand/middleware';

/**
 * Filters State Interface
 * Manages filter preferences across different views
 */
interface FiltersState {
  // User list filters
  userFilters: {
    search: string;
    role: string | null;
    status: string | null;
    sort: string;
  };
  setUserFilters: (filters: Partial<FiltersState['userFilters']>) => void;
  resetUserFilters: () => void;

  // Pagination preferences
  defaultPageSize: number;
  setDefaultPageSize: (size: number) => void;

  // View preferences
  viewMode: 'grid' | 'list' | 'table';
  setViewMode: (mode: 'grid' | 'list' | 'table') => void;
}

const defaultUserFilters = {
  search: '',
  role: null,
  status: null,
  sort: 'createdAt:desc',
};

/**
 * Filters Store (Zustand with Persistence)
 * 
 * Purpose: Persist user preferences for filters and view settings
 * 
 * Use Cases:
 * - Table/list filter states
 * - Sort preferences
 * - Pagination settings
 * - View mode preferences (grid/list/table)
 * 
 * Why Zustand with Persist?
 * - Saves user preferences across sessions
 * - No need to refetch from server
 * - Fast and immediate
 * - Can be reset to defaults easily
 */
export const useFiltersStore = create<FiltersState>()(
  devtools(
    persist(
      (set) => ({
        // User filters
        userFilters: defaultUserFilters,
        setUserFilters: (filters) =>
          set((state) => ({
            userFilters: { ...state.userFilters, ...filters },
          })),
        resetUserFilters: () => set({ userFilters: defaultUserFilters }),

        // Pagination
        defaultPageSize: 10,
        setDefaultPageSize: (size) => set({ defaultPageSize: size }),

        // View mode
        viewMode: 'table',
        setViewMode: (mode) => set({ viewMode: mode }),
      }),
      {
        name: 'filters-store',
      }
    ),
    { name: 'Filters Store' }
  )
);
